// The generic renderer's model: a settings group is a list of entries, each a value with a schema and
// a state, and a way to save them and to read them again. Two sources produce it today — the
// registry's settings screens (`registry-source.ts`) and the venue record
// (`venue-profile/venue-source.ts`) — and a later screen that is "a group of settings" produces it
// too, and gets headings, sources, locks, reset, read-only and the 412 flow for nothing.

import type { ComponentType } from 'react';

import type { Versioned } from '../api/versioned';
import type { ControlHint, Dependency, DisplayUnit } from './layout';
import type { JsonSchema, SchemaContext } from './schema';

/** Where a value comes from (spec/13 §13): the registry default, or the value stored at a scope. */
export type ValueSource = 'default' | 'platform' | 'organization' | 'venue';

export interface RenderableSetting {
  /** The setting's key (`venue.closingAction`) or the resource field's name (`name`). */
  readonly key: string;
  readonly label: string;
  readonly description?: string;
  /** A sub-heading inside the group; consecutive entries with the same one share a panel. */
  readonly section?: string;
  /** What the control is built from. */
  readonly schema: JsonSchema;
  readonly context: SchemaContext;
  /** The value in force at this screen's scope. */
  readonly value: unknown;
  /** Where that value comes from. Absent for a resource's own field, which inherits nothing. */
  readonly source?: ValueSource;
  /** The scope whose lock binds this one (spec/03 §9). A locked value is the value. */
  readonly lockedAt?: 'platform' | 'organization';
  /**
   * HQ's screens only: this screen may lock the value it stores, at this scope — the setting is
   * lockable here (`lockableAt`) and HQ may change it. A lock here is HQ's own, so it does not make
   * the entry read-only: `lockedAt` equal to this is "locked here", and the value can still change.
   */
  readonly lockable?: 'platform' | 'organization';
  /** What "reset to inherited" would give, when this scope stores a value of its own. */
  readonly inherited?: { readonly value: unknown; readonly source: ValueSource };
  /** This scope stores a value of its own — the one a reset removes. */
  readonly hasOverride: boolean;
  /** The member may change it here. */
  readonly editable: boolean;
  /** Why it cannot be changed here, when that is neither a lock nor the member's role. */
  readonly readOnlyReason?: string;
  /**
   * The registry default, for a setting with a display unit (money): the screen names it under the
   * control ("Default: ₹20,000.00"), since the description no longer does. Absent otherwise.
   */
  readonly unitDefault?: { readonly value: unknown };
  /** How many of the venue's station classes and zones store a value of their own (spec/13 §13). */
  readonly placeOverrides?: number;

  // -- Where it sits and how it reads (`layout.ts`, spec/13 §13 as of 2026-10-07). All optional: a resource's own
  //    fields (the venue record) have none, and are drawn as before.

  /** The layout section it sits in (`layout.ts`); with it, the form draws the group's sections in the layout's order. */
  readonly sectionId?: string;
  /** Its place on the screen. */
  readonly order?: number;
  /** Drawn inside the group's collapsed "Advanced" disclosure. */
  readonly advanced?: boolean;
  /** Masters it needs: it nests under the first and collapses while any says it does nothing. */
  readonly dependsOn?: readonly Dependency[];
  /** A master whose value makes it do nothing (it says so). */
  readonly overriddenBy?: Dependency;
  /** Drawn as a sub-option of this setting. */
  readonly under?: string;
  /** How its number is shown and typed (`units.ts`). */
  readonly display?: DisplayUnit;
  /** A purpose-made control. */
  readonly control?: ControlHint;
  /** Why it cannot be changed here, as a kind the row words itself (a lock has its own words). */
  readonly why?: 'role' | 'organization' | 'dated';
  /** The station classes of this venue that keep a value of their own, by name (spec/13 §12). */
  readonly classValues?: readonly { readonly name: string; readonly value: unknown }[];
  /** On the organisation's screen: the venues that keep a value of their own, by name. */
  readonly venueValues?: readonly { readonly name: string; readonly value: unknown }[];
}

/**
 * One change: a value, or a return to the inherited one. On HQ's screens a value may also lock or
 * unlock (`locked`); left out, a stored lock stays as it is. A reset removes the value and its lock.
 */
export type SettingChange =
  | { readonly key: string; readonly value: unknown; readonly reset?: undefined; readonly locked?: boolean }
  | { readonly key: string; readonly reset: true; readonly value?: undefined; readonly locked?: undefined };

/** Words for where a value comes from: the badge, and the same inside a sentence. */
export interface SourceWords {
  readonly badge: (source: ValueSource) => string;
  readonly name: (source: ValueSource) => string;
  /** The row's source line for an inherited value: "Using your organisation's setting — 5 minutes". */
  readonly using?: (source: ValueSource, value: string) => string;
  /** The row's source line for a value stored at this screen's scope: "Changed for this venue". */
  readonly changed?: string;
  /** The reset button: "Reset to your organisation's 5 minutes". */
  readonly resetTo?: (source: ValueSource, value: string) => string;
}

/**
 * One resource a group is drawn from: its entries, the version they were read at, and how to save
 * a change to them and read them again. A group may be drawn from more than one — the Venue profile
 * is the venue record and the registry's `venueProfile` settings — and still has one Save.
 */
export interface SettingsPart {
  readonly id: string;
  readonly entries: readonly RenderableSetting[];
  readonly etag: string;
  /** Send the changes with `If-Match: etag`; resolve with the entries as they are now. */
  readonly save: (changes: readonly SettingChange[], etag: string) => Promise<Versioned<readonly RenderableSetting[]>>;
  /** Read the entries again, as after a `412`. */
  readonly reload: () => Promise<Versioned<readonly RenderableSetting[]>>;
  /** A server field error's `field` → the error key of the control it belongs to (`errorKey`). */
  readonly fieldKey?: (field: string, changes: readonly SettingChange[]) => string | undefined;
  /** A refusal that names no field but is about one — `slug_taken` is about `slug`. */
  readonly problemField?: (code: string) => string | undefined;
}

/** Words for the nested parts of a value: a property, a list item, an option. */
export interface SettingLabels {
  field(entryKey: string, path: readonly string[]): string;
  description(entryKey: string, path: readonly string[], schema: JsonSchema): string | undefined;
  option(entryKey: string, path: readonly string[], value: string | number | boolean): string;
}

/** The props every control gets, including a custom one. */
export interface ControlProps {
  readonly entryKey: string;
  readonly schema: JsonSchema;
  readonly context: SchemaContext;
  /** From the entry to this value, list indices included — where its errors are filed. */
  readonly path: readonly (string | number)[];
  readonly label: string;
  readonly description?: string | undefined;
  readonly value: unknown;
  readonly onChange: (value: unknown) => void;
  readonly disabled: boolean;
  readonly required: boolean;
  readonly nullable: boolean;
  readonly labels: SettingLabels;
  readonly errorAt: (path: readonly (string | number)[]) => string | undefined;
  /** A control holding text it cannot turn into a value yet says so, and the form will not save. */
  readonly onUnparsed?: (key: string, unparsed: boolean) => void;
  readonly custom: Readonly<Record<string, ComponentType<ControlProps>>>;
  /** The value on the screen of another setting, the editor's unsaved one included (spoken warnings read the warnings). */
  readonly valueOf?: (key: string) => unknown;
}

/** `venue.closingAction` + `['mode']` → `venue.closingAction.mode`: how a field error is filed. */
export const errorKey = (entryKey: string, path: readonly (string | number)[]): string =>
  [entryKey, ...path.map(String)].join('.');
